import React, { memo, use, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Label, ListBox, Select } from "@heroui/react";

import { CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";

interface YearSelectProps {
    years: number[];
    value?: number;
    onChange?: (year: number) => void;
    isDisabled?: boolean;
    highlightLastYear?: boolean;
}

export const YearSelect = memo<YearSelectProps>(function YearSelectImpl({
    years = [],
    isDisabled,
    onChange = () => {},
    value,
    highlightLastYear,
}) {
    const intl = useIntl();
    const isLastYear = (year: number) => {
        return highlightLastYear && years.length > 0 && year === Math.max(...years);
    };
    const useFlat = highlightLastYear && isLastYear(value);
    const handleChange = useCallback((key: string | number | null) => onChange(+(key as string)), [onChange]);
    return (
        <Select
            onChange={handleChange}
            value={value !== undefined ? String(value) : ""}
            isDisabled={isDisabled}
            className="w-24 shrink-0"
            aria-label="Year"
            placeholder={intl.formatMessage({ id: "app.components.table.no_items" })}
        >
            <Select.Trigger className={useFlat ? "bg-green-300" : undefined}>
                <Select.Value />
                <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
                <ListBox>
                    {years.map((year) => (
                        <ListBox.Item
                            id={String(year)}
                            key={year}
                            textValue={String(year)}
                            className={isLastYear(year) ? "hover:bg-green-300" : undefined}
                        >
                            <Label>{year}</Label>
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    ))}
                </ListBox>
            </Select.Popover>
        </Select>
    );
});

interface GlobalYearSelectProps {
    disabled?: boolean;
}

export const GlobalYearSelect = memo<GlobalYearSelectProps>(function GlobalYearSelect({ disabled }) {
    const { year, onChangeYear } = use(YearPeriodContext);
    const { companyGQL } = use(CompanyContext);

    const years = useMemo(() => companyGQL?.accountingYears || [], [companyGQL?.accountingYears]);

    return <YearSelect highlightLastYear years={years} isDisabled={disabled} value={year} onChange={onChangeYear} />;
});
